import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Heading4 } from "app/popleads/components/ui-kit/Text";
import { colors } from "app/popleads/components/ui-kit/colors";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spinner from "app/popleads/components/ui-kit/Spinner";
import useGetDealSubCampaigns from "app/popleads/hooks/useGetDealSubCampaigns";
import useGetCampaignSubCampaigns from "app/popleads/hooks/useGetCampaignSubCampaigns";
import useGetDealPaymentsByDealId from "app/popleads/hooks/useGetDealPaymentsByDealId";
import useMoveSubDealToAnotherSubCampaign from "app/popleads/hooks/useMoveSubDealToAnotherSubCampaign";
import useRemoveDealSubCampaign from "app/popleads/hooks/useRemoveDealSubCampaign";
import { useExtensionContext } from "../ExtensionContext";
import { isCipdCampaign } from "../utils/constants";
import ExtensionCampaignAndSubCampaignInput from "./ExtensionCampaignAndSubCampaignInput";
import { toast } from "react-toastify";
import Modal from "app/popleads/components/ui-kit/Modal";
import { FormattedNumber } from "react-intl";

const StyledTitle = styled(Heading4)`
    margin-bottom: 18px;
    color: ${colors.neutral[700]};
    display: block;
`;

const StyledMoveContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
`;

const StyledDisabledText = styled.div`
    color: ${colors.neutral[600]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
`;

const StyledDisabledTextDetail = styled.div`
    color: ${colors.neutral[500]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 11px;
    font-style: italic;
`;

const StyledPaidAmount = styled.span`
    color: ${colors.success[900]};
`;

const StyledDisabledTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 70%;
`;

const StyledButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

const StyledMoveSectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const StyledMoveButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: flex-end;
    margin-top: 12px;
    margin-bottom: 12px;
`;

const StyledLoadingOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;
`;

const StyledSubCampaignsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ExtensionDealExEdCampaigns = ({ setIsCancelModalOpen }) => {
    const { selectedDeal, profile, dealPaymentAdded, setState, refetchDeals } = useExtensionContext();
    const { subCampaigns, getDealSubCampaigns } = useGetDealSubCampaigns(selectedDeal?.id);
    const { getDealPayments } = useGetDealPaymentsByDealId(selectedDeal?.id);
    const [moveSection, setMoveSection] = useState(null);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedSubCampaign, setSelectedSubCampaign] = useState(null);
    const [fetchedSubCampaigns, setFetchedSubCampaigns] = useState([]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [subCampaignToCancel, setSubCampaignToCancel] = useState(null);
    const isCipdDeal = isCipdCampaign(selectedDeal?.campaign);

    const { getCampaignSubCampaigns } = useGetCampaignSubCampaigns();
    const { moveSubDealToAnotherSubCampaign, loading: moveLoading } = useMoveSubDealToAnotherSubCampaign();
    const { removeDealSubCampaign, loading: removeLoading } = useRemoveDealSubCampaign();

    useEffect(() => {
        if (dealPaymentAdded) {
            getDealPayments();
            getDealSubCampaigns();
            setState((prevState) => ({
                ...prevState,
                dealPaymentAdded: false,
            }));
        }
    }, [dealPaymentAdded, getDealPayments, getDealSubCampaigns, setState]);

    useEffect(() => {
        if (selectedCampaign?.id) {
            getCampaignSubCampaigns(selectedCampaign.id)
                .then((campaigns) => {
                    setFetchedSubCampaigns(campaigns || []);
                })
                .catch((error) => {
                    toast.error("Error fetching sub campaigns");
                    setFetchedSubCampaigns([]);
                });
        } else {
            setFetchedSubCampaigns([]);
        }
    }, [selectedCampaign, getCampaignSubCampaigns]);

    useEffect(() => {
        setSelectedSubCampaign(null);
    }, [selectedCampaign]);

    const handleCancelSubCampaign = async () => {
        if (!subCampaignToCancel || !selectedDeal?.id) {
            toast.error("Missing required information");
            return;
        }

        const dealAmount = parseFloat(selectedDeal?.amount || 0);
        const subCampaignAmount = parseFloat(subCampaignToCancel?.amount || 0);

        if (subCampaignAmount > dealAmount) {
            toast.error("Sub campaign amount cannot be higher than the sub remaining amount!");
            setShowCancelModal(false);
            setSubCampaignToCancel(null);
            return;
        }

        const params = {
            dealId: selectedDeal.id,
            campaignId: subCampaignToCancel.campaignId,
        };

        const result = await removeDealSubCampaign(params);

        if (!result.error) {
            setSubCampaignToCancel(null);
            await getDealSubCampaigns();
            await getDealPayments();
            //await refetchProfile();
            await refetchDeals();
        }
        setShowCancelModal(false);
    };

    if (!subCampaigns.length) return null;

    return (
        <>
            {(moveLoading || removeLoading) && (
                <StyledLoadingOverlay>
                    <Spinner size={32} thickness={4} variant="l3rnTech" />
                </StyledLoadingOverlay>
            )}
            <div style={{ marginBottom: "12px", position: "relative" }}>
                <StyledTitle>Sub Deals</StyledTitle>
                <StyledSubCampaignsContainer>
                    {subCampaigns.map((subCampaign, index) => (
                        <React.Fragment key={index}>
                            <StyledMoveContainer>
                                <StyledDisabledTextContainer>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{subCampaign.campaignName}</Tooltip>}
                                    >
                                        <StyledDisabledText>{subCampaign.campaignName}</StyledDisabledText>
                                    </OverlayTrigger>
                                    <StyledDisabledTextDetail>
                                        {"- Paid: "}
                                        <StyledPaidAmount>
                                            $
                                            <FormattedNumber
                                                minimumFractionDigits={0}
                                                maximumFractionDigits={2}
                                                value={subCampaign?.paidAmount}
                                            />
                                        </StyledPaidAmount>
                                    </StyledDisabledTextDetail>
                                </StyledDisabledTextContainer>
                                {moveSection !== index && (
                                    <StyledButtonContainer>
                                        {selectedDeal?.dealCampaignType !== "Public-ExEd" && !isCipdDeal && (
                                            <ButtonWithText
                                                size="xsmall"
                                                variant="l3rnTech"
                                                buttonStyle="borderless"
                                                onClick={() => {
                                                    setMoveSection(index);
                                                    setSelectedCampaign(null);
                                                    setSelectedSubCampaign(null);
                                                    setFetchedSubCampaigns([]);
                                                }}
                                            >
                                                move
                                            </ButtonWithText>
                                        )}
                                        {parseFloat(subCampaign.paidAmount) === 0 && (
                                            <ButtonWithText
                                                variant="l3rnTech"
                                                buttonStyle="borderless"
                                                type="default"
                                                size="xsmall"
                                                onClick={() => {
                                                    setSubCampaignToCancel(subCampaign);
                                                    setShowCancelModal(true);
                                                }}
                                            >
                                                cancel
                                            </ButtonWithText>
                                        )}
                                    </StyledButtonContainer>
                                )}
                            </StyledMoveContainer>
                            {moveSection === index && selectedDeal?.dealCampaignType !== "Public-ExEd" && (
                                <>
                                    <StyledMoveSectionContainer>
                                        <ExtensionCampaignAndSubCampaignInput
                                            mode="master"
                                            optionClick={(campaign) => {
                                                setSelectedCampaign(campaign);
                                            }}
                                            campaignType={selectedDeal?.dealCampaignType}
                                            placeholder={"Search Campaign"}
                                            excludeMainCampaign={selectedDeal?.campaign?.ad}
                                        />
                                        <ExtensionCampaignAndSubCampaignInput
                                            mode="sub"
                                            disabled={!selectedCampaign}
                                            masterId={selectedCampaign?.id}
                                            preloadedSubCampaigns={fetchedSubCampaigns}
                                            optionClick={(subCampaign) => {
                                                setSelectedSubCampaign(subCampaign);
                                            }}
                                            placeholder={"Search Sub Campaign"}
                                        />
                                    </StyledMoveSectionContainer>
                                    <StyledMoveButtonContainer>
                                        <ButtonWithText
                                            label="Confirm move"
                                            icon="check"
                                            iconType="feather"
                                            variant="l3rnTech"
                                            size="circle"
                                            buttonStyle="borderless"
                                            disabled={moveLoading}
                                            onClick={async () => {
                                                const params = {
                                                    profileId: profile?.id,
                                                    removedSubCampaignId: subCampaign.campaignId,
                                                    movedSubCampaignId: selectedSubCampaign?.id,
                                                    movedMasterCampaignId: selectedCampaign?.id,
                                                    dealId: selectedDeal?.id,
                                                };
                                                if (!params.movedSubCampaignId) {
                                                    toast.error("Please Select campaign and sub campaign!");
                                                    return;
                                                }

                                                const result = await moveSubDealToAnotherSubCampaign(params);

                                                if (!result.error) {
                                                    setMoveSection(null);
                                                    setSelectedCampaign(null);
                                                    setSelectedSubCampaign(null);
                                                    setFetchedSubCampaigns([]);
                                                    //await refetchProfile();
                                                    await refetchDeals();
                                                    await getDealSubCampaigns();
                                                    await getDealPayments();
                                                }
                                            }}
                                        />
                                        <ButtonWithText
                                            label="Cancel move"
                                            icon="x"
                                            iconType="feather"
                                            variant="danger"
                                            size="circle"
                                            buttonStyle="borderless"
                                            onClick={() => {
                                                setMoveSection(null);
                                                setSelectedCampaign(null);
                                                setSelectedSubCampaign(null);
                                                setFetchedSubCampaigns([]);
                                            }}
                                        />
                                    </StyledMoveButtonContainer>
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </StyledSubCampaignsContainer>
            </div>

            <Modal
                isOpen={showCancelModal}
                onClose={() => {
                    setShowCancelModal(false);
                    setSubCampaignToCancel(null);
                }}
                title="Are you sure you want to cancel?"
                variant="danger"
                icon="delete"
                successButtonText="Yes"
                cancelButtonText="No"
                onSuccess={handleCancelSubCampaign}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionDealExEdCampaigns;
